import React from "react";
import { motion } from "framer-motion";
import PageHeader from "../../components/PageHeader";
import { useQuery } from "@tanstack/react-query";
import { axiosget } from "../../helpers/httprequest";
import BtnUnifiedPayment from "../../buttons/BtnUnifiedPayment";
import Loader from "../../components/Loader";
import moment from "../../helpers/moment";
import is_money from "../../helpers/is_money";
import VerifyPayment from "./VerifyPayment";
import PrintReceipt from "./PrintReceipt";
import ApplyWaiverCode from "./ApplyWaiverCode";
import StartNewApplication from "../NewApplication/StartNewApplication";
import { Badge } from "../../components/ui/Badge";
import EmptyState from "../../components/ui/EmptyState";
import { Alert, AlertDescription } from "../../components/ui/Alert";
import {
  FiDollarSign,
  FiCheckCircle,
  FiClock,
  FiFileText,
  FiHash,
  FiCreditCard,
} from "react-icons/fi";

const PayFee = ({ wizardMode = false, quickApplySessionId }) => {
  const { data, isLoading, error } = useQuery({
    queryKey: ["generatedRRR"],
    queryFn: async () => {
      const req = await axiosget("ms_application_fee");
      return req?.data;
    },
  });

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.08 },
    },
  };

  const rowVariants = {
    hidden: { opacity: 0, y: 10 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.3 },
    },
  };

  return (
    <>
      {!wizardMode && <PageHeader
        title="Application Fees"
        subtitle="Manage payments, verify transactions, and print receipts"
        icon={<FiCreditCard className="w-5 h-5" />}
      />}

      {wizardMode ? (
        <>
          {/* In wizard mode, filter payments by session ID */}
          {(() => {
            const sessionPayments = data && data?.type === undefined && quickApplySessionId
              ? data.filter((v) => v?.trxdata?.quickApplySessionId === quickApplySessionId)
              : [];
            const hasSessionPayment = sessionPayments.length > 0;

            return (
              <>
                {!hasSessionPayment && (
                  <BtnUnifiedPayment inline quickApplySessionId={quickApplySessionId} />
                )}
              </>
            );
          })()}

          {isLoading && <Loader />}

          {/* Show only payments for this session */}
          {data && data?.type === undefined && (() => {
            const actionable = quickApplySessionId
              ? data.filter((v) => v?.trxdata?.quickApplySessionId === quickApplySessionId)
              : data.filter((v) => !v?.application);
            if (actionable.length === 0) return null;
            return (
              <motion.div
                variants={containerVariants}
                initial="hidden"
                animate="visible"
                className="space-y-3 mt-4"
              >
                {actionable.map((v, i) => (
                  <motion.div
                    key={`_wizard_payment_${i}`}
                    variants={rowVariants}
                    className={`bg-white rounded-xl border overflow-hidden ${
                      v?.isverified ? "border-success-200" : "border-neutral-200"
                    }`}
                  >
                    <div className="px-5 py-4">
                      <div className="flex items-center justify-between flex-wrap gap-3">
                        <div className="flex items-center gap-4 min-w-0 flex-1">
                          <div className={`p-2.5 rounded-lg flex-shrink-0 ${
                            v?.isverified ? "bg-success-50" : "bg-neutral-100"
                          }`}>
                            {v?.isverified ? (
                              <FiCheckCircle className="h-5 w-5 text-success-600" />
                            ) : (
                              <FiClock className="h-5 w-5 text-neutral-500" />
                            )}
                          </div>
                          <div className="min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="text-sm font-semibold text-neutral-900 font-mono">
                                {v?.trxcode}
                              </span>
                              <Badge
                                variant={v?.isverified ? "success" : "warning"}
                                dot
                                size="sm"
                              >
                                {v?.isverified ? "Verified" : "Pending"}
                              </Badge>
                            </div>
                            <div className="flex items-center gap-2 mt-0.5">
                              <span className="text-xs text-neutral-500">
                                {v?.trxdata?.gateway ? v.trxdata.gatewayDisplayName || v.trxdata.gateway : "Remita RRR"}
                              </span>
                              <span className="text-neutral-300">|</span>
                              <span className="text-xs text-neutral-400">
                                {moment(v.createdAt)} ago
                              </span>
                            </div>
                          </div>
                        </div>
                        <div className="text-right flex-shrink-0">
                          <p className="text-lg font-bold text-neutral-900">
                            {is_money(v?.amount)}
                          </p>
                        </div>
                      </div>
                      {v?.trxdata?.description && (
                        <div className="mt-3 flex items-center gap-2 text-xs text-neutral-500">
                          <FiFileText className="h-3.5 w-3.5 flex-shrink-0" />
                          <span className="truncate">{v.trxdata.description}</span>
                        </div>
                      )}
                    </div>
                    <div className="px-5 py-3 bg-neutral-50 border-t border-neutral-100 flex items-center justify-end gap-2 flex-wrap">
                      {v?.isverified ? (
                        <Badge variant="success" size="md">
                          <FiCheckCircle className="h-4 w-4 mr-1" /> Payment Confirmed
                        </Badge>
                      ) : (
                        <>
                          {v?.trxdata?.paymentUrl && (
                            <a
                              href={v.trxdata.paymentUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center justify-center gap-2 px-3 py-1.5 text-sm font-medium rounded-lg bg-green-600 text-white hover:bg-green-700 transition-colors"
                            >
                              <FiDollarSign className="h-4 w-4" />
                              Make Payment
                            </a>
                          )}
                          <VerifyPayment v={v} />
                          <ApplyWaiverCode v={v} />
                        </>
                      )}
                    </div>
                  </motion.div>
                ))}
              </motion.div>
            );
          })()}
        </>
      ) : (
        <>
          {isLoading && <Loader />}

          {error && (
            <Alert variant="danger" className="mb-6">
              <AlertDescription>Local server error: {error?.message || error}</AlertDescription>
            </Alert>
          )}

          {data?.type !== undefined && (
            <Alert variant="danger" className="mb-6">
              <AlertDescription>{data?.message}</AlertDescription>
            </Alert>
          )}

          {!isLoading && data && data?.type === undefined && data.length === 0 && (
            <EmptyState
              variant="no-data"
              title="No Payment History"
              description="You haven't made any application fee payments yet. Generate a payment to get started."
              icon={<FiDollarSign className="h-12 w-12" />}
            />
          )}

          {data && data?.type === undefined && data.length > 0 && (
            <motion.div
              variants={containerVariants}
              initial="hidden"
              animate="visible"
              className="space-y-3 mb-6"
            >
              {data.map((v, i) => (
                <motion.div
                  key={`_payment_${i}`}
                  variants={rowVariants}
                  className={`bg-white rounded-xl border overflow-hidden ${
                    v?.isverified ? "border-success-200" : "border-neutral-200"
                  }`}
                >
                  {/* Main Row */}
                  <div className="px-5 py-4">
                    <div className="flex items-center justify-between flex-wrap gap-3">
                      {/* Left: Reference + Gateway */}
                      <div className="flex items-center gap-4 min-w-0 flex-1">
                        <div className={`p-2.5 rounded-lg flex-shrink-0 ${
                          v?.isverified ? "bg-success-50" : "bg-neutral-100"
                        }`}>
                          {v?.isverified ? (
                            <FiCheckCircle className="h-5 w-5 text-success-600" />
                          ) : (
                            <FiClock className="h-5 w-5 text-neutral-500" />
                          )}
                        </div>
                        <div className="min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-sm font-semibold text-neutral-900 font-mono">
                              {v?.trxcode}
                            </span>
                            <Badge
                              variant={v?.isverified ? "success" : "warning"}
                              dot
                              size="sm"
                            >
                              {v?.isverified ? "Verified" : "Pending"}
                            </Badge>
                            {v?.application && (
                              <Badge variant="info" size="sm">Used</Badge>
                            )}
                          </div>
                          <div className="flex items-center gap-2 mt-0.5">
                            <span className="text-xs text-neutral-500">
                              {v?.trxdata?.gateway ? v.trxdata.gatewayDisplayName || v.trxdata.gateway : "Remita RRR"}
                            </span>
                            {v?.trxdata?.internalReference && (
                              <>
                                <span className="text-neutral-300">|</span>
                                <span className="text-xs text-neutral-400 font-mono truncate">
                                  Ref: {v.trxdata.internalReference}
                                </span>
                              </>
                            )}
                            <span className="text-neutral-300">|</span>
                            <span className="text-xs text-neutral-400">
                              {moment(v.createdAt)} ago
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Right: Amount */}
                      <div className="text-right flex-shrink-0">
                        <p className="text-lg font-bold text-neutral-900">
                          {is_money(v?.amount)}
                        </p>
                      </div>
                    </div>

                    {/* Description */}
                    {v?.trxdata?.description && (
                      <div className="mt-3 flex items-center gap-2 text-xs text-neutral-500">
                        <FiFileText className="h-3.5 w-3.5 flex-shrink-0" />
                        <span className="truncate">{v.trxdata.description}</span>
                      </div>
                    )}
                  </div>

                  {/* Actions Footer */}
                  <div className="px-5 py-3 bg-neutral-50 border-t border-neutral-100 flex items-center justify-end gap-2 flex-wrap">
                    {v?.isverified ? (
                      <>
                        <PrintReceipt v={v} />
                        {!v?.application && <StartNewApplication v={v} />}
                      </>
                    ) : (
                      <>
                        {v?.trxdata?.paymentUrl && (
                          <a
                            href={v.trxdata.paymentUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center justify-center gap-2 px-3 py-1.5 text-sm font-medium rounded-lg bg-green-600 text-white hover:bg-green-700 transition-colors"
                          >
                            <FiDollarSign className="h-4 w-4" />
                            Make Payment
                          </a>
                        )}
                        <VerifyPayment v={v} />
                        <ApplyWaiverCode v={v} />
                      </>
                    )}
                  </div>
                </motion.div>
              ))}
            </motion.div>
          )}

          <BtnUnifiedPayment />
        </>
      )}
    </>
  );
};

export default PayFee;
